// Per-tenant IndexedDB (Dexie) holding the offline snapshot.
// Table/index changes → bump version() with an upgrade; the search index has
// its own INDEX_FORMAT_VERSION (step 4) and never needs a Dexie migration.
import Dexie from "dexie";
import { isEncrypted, dbPrefix } from "./secure.js";

export const DB_SCHEMA_VERSION = 1;
export const dbName = (tenant) => `${dbPrefix()}${tenant}`;

const instances = new Map();   // per tenant; the mode is fixed per page load (set at boot)

export function openOfflineDb(tenant) {
  if (!tenant) return null;
  if (instances.has(tenant)) return instances.get(tenant);
  const db = new Dexie(dbName(tenant));
  if (isEncrypted()) {
    // Encrypted copy (secure.js): only ids, partId, hashes and order in clear;
    // documentTypes keyed by order (the type name is sealed); no sections table.
    db.version(1).stores({
      meta: "key",
      documents: "docId, partId, contentHash, familyKey",
      parts: "id, parent_id",
      documentTypes: "order",
      chunks: "chunkId, docId",
      searchIndex: "name",
      fileState: "key, docId, kind, contentHash",              // key = opaque /od/f/… path
    });
    instances.set(tenant, db);
    return db;
  }
  db.version(1).stores({
    meta: "key",                                              // {key, value}
    documents: "docId, partId, displayName, contentHash, familyKey",
    parts: "id, parent_id",
    documentTypes: "type, order",
    sections: "sectionId, docId",
    chunks: "chunkId, docId",
    searchIndex: "name",                                      // {name:'docs'|'chunks', json, builtAt, formatVersion, …}
    fileState: "key, docId, kind, contentHash",               // written by the file cache (step 3)
  });
  instances.set(tenant, db);
  return db;
}

export async function deleteOfflineDb(tenant) {
  if (!tenant) return;
  const db = instances.get(tenant);
  if (db) { try { db.close(); } catch { /* ignore */ } instances.delete(tenant); }
  try { await Dexie.delete(dbName(tenant)); } catch { /* ignore */ }
}

export async function getMeta(db, key, fallback = null) {
  const row = await db.meta.get(key);
  return row ? row.value : fallback;
}

export async function setMeta(db, key, value) {
  await db.meta.put({ key, value });
}

// Strips offline-only fields so callers get exactly the /api/documents shape.
const OFFLINE_ONLY = new Set(["contentHash", "metaSig", "familyKey", "keyTokens", "synonyms", "sourceFile"]);
export function toApiDocument(row) {
  const out = {};
  for (const [k, v] of Object.entries(row)) if (!OFFLINE_ONLY.has(k)) out[k] = v;
  return out;
}
